import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, LogOut, Plus, Edit, Trash2, X, Check, Shield, Users, 
  Key, Eye, EyeOff, RefreshCw, ToggleLeft, ToggleRight, Clock,
  FileText, ChevronDown, ChevronUp, UserCog, Activity
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

import { BACKEND_URL } from '../config/api';

const ADMIN_ROLE_LABELS = {
  super_admin: { label: 'Super Admin', color: 'bg-red-100 text-red-800' },
  admin: { label: 'Admin', color: 'bg-blue-100 text-blue-800' },
  moderator: { label: 'Moderator', color: 'bg-green-100 text-green-800' }
};

const FEATURE_TOGGLE_LABELS = {
  tradesperson_subscription: 'Tradesperson Subscription',
  employer_chat_credits: 'Employer Chat Credits',
  verification_badges: 'Verification Badges',
  region_lock_waiting_list: 'Region Lock / Waiting List',
  reviews_enabled: 'Reviews'
};

export default function AdminManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [featureToggles, setFeatureToggles] = useState({});
  const [activeTab, setActiveTab] = useState('admins');
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    admin_role: 'admin'
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/my-permissions`, {
        withCredentials: true
      });
      setCurrentAdmin(response.data);
      
      if (response.data.is_super_admin) {
        fetchAdmins();
      }
      fetchFeatureToggles();
      fetchAuditLogs();
    } catch (error) {
      if (error.response?.status === 403 || error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/admins`, {
        withCredentials: true
      });
      setAdmins(response.data);
    } catch (error) {
      console.error('Failed to fetch admins');
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/audit-log?limit=50`, {
        withCredentials: true
      });
      setAuditLogs(response.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch audit logs');
    }
  };

  const fetchFeatureToggles = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/feature-toggles`, {
        withCredentials: true
      });
      setFeatureToggles(response.data);
    } catch (error) {
      console.error('Failed to fetch feature toggles');
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/admin/admins`, formData, {
        withCredentials: true
      });
      toast.success('Admin created successfully');
      setShowAddModal(false);
      setFormData({ name: '', email: '', password: '', admin_role: 'admin' });
      fetchAdmins();
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    }
  };

  const handleEditAdmin = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/admins/${selectedAdmin.user_id}`, {
        name: formData.name,
        email: formData.email,
        admin_role: formData.admin_role
      }, { withCredentials: true });
      toast.success('Admin updated successfully');
      setShowEditModal(false);
      setSelectedAdmin(null);
      fetchAdmins();
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update admin');
    }
  };

  const handleDeactivateAdmin = async (admin) => {
    if (!window.confirm(`Are you sure you want to deactivate ${admin.name}?`)) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/admins/${admin.user_id}`, {
        withCredentials: true
      });
      toast.success('Admin deactivated');
      fetchAdmins();
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate admin');
    }
  };

  const handleReactivateAdmin = async (admin) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/admins/${admin.user_id}/reactivate`, {}, {
        withCredentials: true
      });
      toast.success('Admin reactivated');
      fetchAdmins();
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate admin');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    try {
      await axios.post(`${BACKEND_URL}/api/admin/admins/${selectedAdmin.user_id}/reset-password`, {
        new_password: passwordData.new_password
      }, { withCredentials: true });
      toast.success('Password reset successfully');
      setShowPasswordModal(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleChangeOwnPassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    try {
      await axios.post(`${BACKEND_URL}/api/admin/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, { withCredentials: true });
      toast.success('Password changed successfully');
      setShowChangePasswordModal(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleToggleFeature = async (featureKey) => {
    if (!currentAdmin?.is_super_admin) {
      toast.error('Only Super Admin can change feature toggles');
      return;
    }
    
    try {
      const newValue = !featureToggles[featureKey];
      await axios.patch(`${BACKEND_URL}/api/admin/feature-toggles`, {
        [featureKey]: newValue
      }, { withCredentials: true });
      
      setFeatureToggles(prev => ({ ...prev, [featureKey]: newValue }));
      toast.success(`${FEATURE_TOGGLE_LABELS[featureKey]} ${newValue ? 'enabled' : 'disabled'}`);
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update feature toggle');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openEditModal = (admin) => {
    setSelectedAdmin(admin);
    setFormData({
      name: admin.name,
      email: admin.email,
      admin_role: admin.admin_role || 'admin',
      password: ''
    });
    setShowEditModal(true);
  };

  const openPasswordResetModal = (admin) => {
    setSelectedAdmin(admin);
    setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    setShowPasswordModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="flex items-center gap-2">
              <Building2 className="h-6 w-6 text-orange-500" />
              <h1 className="text-xl font-bold text-slate-900">Admin Management</h1>
            </button>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <span className="text-sm text-slate-600">Hi, {currentAdmin?.name}</span>
              <span className={`ml-2 px-2 py-1 text-xs rounded-full ${ADMIN_ROLE_LABELS[currentAdmin?.admin_role]?.color || 'bg-slate-100'}`}>
                {ADMIN_ROLE_LABELS[currentAdmin?.admin_role]?.label || 'Admin'}
              </span>
            </div>
            <button onClick={handleLogout} className="btn-secondary text-sm flex items-center gap-2">
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-slate-200">
          {currentAdmin?.is_super_admin && (
            <button
              onClick={() => setActiveTab('admins')}
              className={`pb-3 px-1 text-sm font-medium transition-colors ${
                activeTab === 'admins' 
                  ? 'text-orange-600 border-b-2 border-orange-600' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Users className="h-4 w-4 inline mr-2" />
              Admin Accounts
            </button>
          )}
          <button
            onClick={() => setActiveTab('toggles')}
            className={`pb-3 px-1 text-sm font-medium transition-colors ${
              activeTab === 'toggles' 
                ? 'text-orange-600 border-b-2 border-orange-600' 
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <ToggleRight className="h-4 w-4 inline mr-2" />
            Feature Toggles
          </button>
          <button
            onClick={() => setActiveTab('audit')}
            className={`pb-3 px-1 text-sm font-medium transition-colors ${
              activeTab === 'audit' 
                ? 'text-orange-600 border-b-2 border-orange-600' 
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Activity className="h-4 w-4 inline mr-2" />
            Audit Log
          </button>
          <button
            onClick={() => setShowChangePasswordModal(true)}
            className="pb-3 px-1 text-sm font-medium text-slate-600 hover:text-slate-900 ml-auto"
          >
            <Key className="h-4 w-4 inline mr-2" />
            Change My Password
          </button>
        </div>

        {/* Admin Accounts Tab */}
        {activeTab === 'admins' && currentAdmin?.is_super_admin && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Admin Accounts</h2>
              <button
                onClick={() => {
                  setFormData({ name: '', email: '', password: '', admin_role: 'admin' });
                  setShowAddModal(true);
                }}
                className="btn-primary flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Admin
              </button>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {admins.map((admin) => (
                    <tr key={admin.user_id} className={!admin.is_active ? 'bg-slate-50 opacity-60' : ''}>
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-900">{admin.name}</span>
                        {admin.user_id === currentAdmin?.user_id && (
                          <span className="ml-2 text-xs text-slate-500">(You)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{admin.email}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${ADMIN_ROLE_LABELS[admin.admin_role]?.color || 'bg-slate-100'}`}>
                          {ADMIN_ROLE_LABELS[admin.admin_role]?.label || 'Admin'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {admin.is_active ? (
                          <span className="text-green-600 text-sm">Active</span>
                        ) : (
                          <span className="text-red-600 text-sm">Inactive</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditModal(admin)}
                            className="p-1 text-slate-400 hover:text-blue-600"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openPasswordResetModal(admin)}
                            className="p-1 text-slate-400 hover:text-orange-600"
                            title="Reset Password"
                          >
                            <Key className="h-4 w-4" />
                          </button>
                          {admin.user_id !== currentAdmin?.user_id && (
                            admin.is_active ? (
                              <button
                                onClick={() => handleDeactivateAdmin(admin)}
                                className="p-1 text-slate-400 hover:text-red-600"
                                title="Deactivate"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleReactivateAdmin(admin)}
                                className="p-1 text-slate-400 hover:text-green-600"
                                title="Reactivate"
                              >
                                <RefreshCw className="h-4 w-4" />
                              </button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Feature Toggles Tab */}
        {activeTab === 'toggles' && (
          <div>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Feature Toggles</h2>
              <p className="text-sm text-slate-600">Enable or disable platform features instantly</p>
              {!currentAdmin?.is_super_admin && (
                <p className="text-sm text-orange-600 mt-1">Only Super Admin can modify toggles</p>
              )}
            </div>

            <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
              {Object.entries(FEATURE_TOGGLE_LABELS).map(([key, label]) => (
                <div key={key} className="px-4 py-4 flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-slate-900">{label}</h3>
                    <p className="text-sm text-slate-500">
                      {featureToggles[key] ? 'Currently enabled' : 'Currently disabled'}
                    </p>
                  </div>
                  <button
                    onClick={() => handleToggleFeature(key)}
                    disabled={!currentAdmin?.is_super_admin}
                    className={`transition-colors ${
                      !currentAdmin?.is_super_admin ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {featureToggles[key] ? (
                      <ToggleRight className="h-8 w-8 text-green-500" />
                    ) : (
                      <ToggleLeft className="h-8 w-8 text-slate-400" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Audit Log Tab */}
        {activeTab === 'audit' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Audit Log</h2>
                <p className="text-sm text-slate-600">Record of admin actions</p>
              </div>
              <button onClick={fetchAuditLogs} className="btn-secondary text-sm">
                <RefreshCw className="h-4 w-4 inline mr-1" />
                Refresh
              </button>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Timestamp</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Admin</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Target</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {auditLogs.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-slate-500">
                        No audit logs found
                      </td>
                    </tr>
                  ) : (
                    auditLogs.map((log) => (
                      <tr key={log.audit_id}>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {formatTimestamp(log.timestamp)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-slate-900">{log.admin_name}</div>
                          <div className="text-xs text-slate-500">{log.admin_role}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 text-xs rounded bg-slate-100 text-slate-700">
                            {log.action?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {log.target_type && (
                            <span>{log.target_type}: {log.target_id?.slice(0, 12)}...</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500 max-w-xs truncate">
                          {log.details && Object.keys(log.details).length > 0 
                            ? JSON.stringify(log.details).slice(0, 50) + '...'
                            : '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Add Admin Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Admin</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddAdmin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg pr-10"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
              <select
                value={formData.admin_role}
                onChange={(e) => setFormData({ ...formData, admin_role: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="moderator">Moderator</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <button type="button" onClick={() => setShowAddModal(false)} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                Create Admin
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Admin Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Admin</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditAdmin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
              <select
                value={formData.admin_role}
                onChange={(e) => setFormData({ ...formData, admin_role: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="moderator">Moderator</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <button type="button" onClick={() => setShowEditModal(false)} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                Save Changes
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal (for other admins) */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password for {selectedAdmin?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg pr-10"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Confirm New Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                required
                minLength={6}
              />
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <button type="button" onClick={() => setShowPasswordModal(false)} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                Reset Password
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Own Password Modal */}
      <Dialog open={showChangePasswordModal} onOpenChange={setShowChangePasswordModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Your Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangeOwnPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Current Password</label>
              <input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg pr-10"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Confirm New Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                required
                minLength={6}
              />
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <button type="button" onClick={() => setShowChangePasswordModal(false)} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                Change Password
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
